import React, { useState } from 'react'
import { supabase } from '../lib/supabase'

// ── Login — OTP (email code) flow ─────────────────────────────────────────────
//
// Step 1 — Email entry: member enters their email, we call signInWithOtp.
//           Supabase emails a 6-digit code (valid 10 minutes).
// Step 2 — Code entry: member enters the code, we call verifyOtp.
//           On success, onAuthStateChange fires SIGNED_IN in App.jsx,
//           which calls resolveUserType and routes to the dashboard.
//
// No passwords are ever set or stored. Sessions persist in localStorage
// via Supabase's rotating refresh token — members stay signed in across
// browser sessions until they clear their cache or use a new device.

const linkBtn = {
  background:     'none',
  border:         'none',
  color:          'var(--muted)',
  cursor:         'pointer',
  textDecoration: 'underline',
  fontSize:       '0.85rem',
  padding:        0,
}

export default function Login() {
  const [email,   setEmail]   = useState('')
  const [code,    setCode]    = useState('')
  // 'email' → waiting for email input
  // 'code'  → code sent, waiting for code input
  const [step,    setStep]    = useState('email')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)
  const [resent,  setResent]  = useState(false)

  // ── Step 1: send OTP code ─────────────────────────────────────────────────────
  async function handleSendCode(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: {
        // Creates a Supabase auth user if one doesn't exist yet for this email.
        // The resolveUserType function in App.jsx then links them to their
        // member row (created by poll-orders) via the link_member_to_auth_user RPC.
        shouldCreateUser: true,
      },
    })

    if (error) {
      setError(error.message)
    } else {
      setStep('code')
      setResent(false)
    }
    setLoading(false)
  }

  // ── Step 2: verify OTP code ───────────────────────────────────────────────────
  async function handleVerifyCode(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: code.trim(),
      type:  'email',
    })

    if (error) {
      const msg = error.message.toLowerCase()
      if (msg.includes('expired')) {
        setError('That code has expired. Use the link below to request a new one.')
      } else if (msg.includes('invalid') || msg.includes('otp')) {
        setError('Incorrect code — please check and try again.')
      } else {
        setError(error.message)
      }
    }
    // On success: onAuthStateChange in App.jsx fires SIGNED_IN automatically.
    // No explicit routing needed here.
    setLoading(false)
  }

  // ── Resend code ───────────────────────────────────────────────────────────────
  async function handleResend() {
    setError(null)
    setResent(false)
    setLoading(true)
    setCode('')

    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: { shouldCreateUser: true },
    })

    if (error) {
      setError(error.message)
    } else {
      setResent(true)
    }
    setLoading(false)
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-box">

        <div className="login-brand">
          <h1>Flock Credit</h1>
          <p>Shepherd's Hut Wines member portal</p>
        </div>

        <div className="card">
          {error  && <div className="alert alert-error"  style={{ marginBottom: '1rem' }}>{error}</div>}
          {resent && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>A new code has been sent to {email}.</div>}

          {/* ── Step 1: email entry ──────────────────────────────────────────── */}
          {step === 'email' && (
            <form onSubmit={handleSendCode}>
              <div className="form-group">
                <label className="form-label" htmlFor="otp-email">Email address</label>
                <input
                  id="otp-email"
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
              >
                {loading ? 'Sending…' : 'Verify Email'}
              </button>
            </form>
          )}

          {/* ── Step 2: code entry ───────────────────────────────────────────── */}
          {step === 'code' && (
            <form onSubmit={handleVerifyCode}>
              <p className="text-sm text-muted" style={{ marginBottom: '1.25rem', lineHeight: 1.7 }}>
                We've sent a 6-digit code to <strong>{email}</strong>.
                Enter it below — it's valid for 10 minutes.
              </p>

              <div className="form-group">
                <label className="form-label" htmlFor="otp-code">Verification code</label>
                <input
                  id="otp-code"
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  style={{ letterSpacing: '0.35em', fontSize: '1.25rem', textAlign: 'center' }}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading || code.length < 6}
              >
                {loading ? 'Verifying…' : 'Sign in'}
              </button>

              <div style={{
                display:        'flex',
                justifyContent: 'space-between',
                alignItems:     'center',
                marginTop:      '1rem',
                flexWrap:       'wrap',
                gap:            '0.5rem',
              }}>
                <button
                  type="button"
                  style={linkBtn}
                  onClick={() => { setStep('email'); setCode(''); setError(null); setResent(false) }}
                >
                  ← Different email
                </button>
                <button
                  type="button"
                  style={linkBtn}
                  onClick={handleResend}
                  disabled={loading}
                >
                  {loading ? 'Sending…' : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="text-sm text-muted" style={{ textAlign: 'center', marginTop: '1rem', lineHeight: 1.6 }}>
          Use the same email address you used to create your Shepherd's Hut Wines subscription to reveal and use your credit.
        </p>

      </div>
    </div>
  )
}
